'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Search, RefreshCw, Loader2, ChevronDown, Filter, Key, Trash2, PlayCircle, PauseCircle, Edit2, Bell, MessageSquare, Package, Shield, LogIn, LogOut, CheckCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface AdminActionLog {
  id: string;
  created_at: string;
  admin_id: string | null;
  admin_email: string | null;
  action_type: string;
  target_type: string | null;
  target_id: string | null;
  target_name: string | null;
  description: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  status: string;
}

const ACTION_CONFIG: Record<string, { labelAr: string; labelEn: string; icon: React.ElementType; color: string; bg: string }> = {
  company_activated:         { labelAr: 'تفعيل شركة',         labelEn: 'Company Activated',       icon: PlayCircle,   color: 'text-green-600',  bg: 'bg-green-50' },
  company_suspended:         { labelAr: 'تعليق شركة',         labelEn: 'Company Suspended',       icon: PauseCircle,  color: 'text-red-600',    bg: 'bg-red-50' },
  company_deleted:           { labelAr: 'حذف شركة',           labelEn: 'Company Deleted',         icon: Trash2,       color: 'text-red-700',    bg: 'bg-red-50' },
  company_password_change:   { labelAr: 'تغيير كلمة مرور',    labelEn: 'Password Changed',        icon: Key,          color: 'text-amber-600',  bg: 'bg-amber-50' },
  company_edited:            { labelAr: 'تعديل شركة',         labelEn: 'Company Edited',          icon: Edit2,        color: 'text-blue-600',   bg: 'bg-blue-50' },
  join_request_approved:     { labelAr: 'قبول طلب انضمام',    labelEn: 'Request Approved',        icon: CheckCircle,  color: 'text-green-600',  bg: 'bg-green-50' },
  join_request_rejected:     { labelAr: 'رفض طلب انضمام',     labelEn: 'Request Rejected',        icon: Trash2,       color: 'text-red-600',    bg: 'bg-red-50' },
  join_request_info_required:{ labelAr: 'طلب معلومات إضافية', labelEn: 'Info Required',           icon: MessageSquare,color: 'text-amber-600',  bg: 'bg-amber-50' },
  package_created:           { labelAr: 'إنشاء باقة',         labelEn: 'Package Created',         icon: Package,      color: 'text-purple-600', bg: 'bg-purple-50' },
  package_updated:           { labelAr: 'تحديث باقة',         labelEn: 'Package Updated',         icon: Package,      color: 'text-blue-600',   bg: 'bg-blue-50' },
  notification_sent:         { labelAr: 'إرسال إشعار',        labelEn: 'Notification Sent',       icon: Bell,         color: 'text-teal-600',   bg: 'bg-teal-50' },
  complaint_resolved:        { labelAr: 'حل شكوى',            labelEn: 'Complaint Resolved',      icon: CheckCircle,  color: 'text-green-600',  bg: 'bg-green-50' },
  auth_alert_resolved:       { labelAr: 'حل تنبيه أمني',      labelEn: 'Alert Resolved',          icon: Shield,       color: 'text-teal-600',   bg: 'bg-teal-50' },
  admin_login:               { labelAr: 'دخول المدير',         labelEn: 'Admin Login',             icon: LogIn,        color: 'text-gray-600',   bg: 'bg-gray-50' },
  admin_logout:              { labelAr: 'خروج المدير',         labelEn: 'Admin Logout',            icon: LogOut,       color: 'text-gray-600',   bg: 'bg-gray-50' },
};

const getActionConfig = (type: string) =>
  ACTION_CONFIG[type] ?? { labelAr: type, labelEn: type, icon: Activity, color: 'text-gray-600', bg: 'bg-gray-50' };

const PAGE_SIZE = 30;

const formatDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('ar-SA', { dateStyle: 'short', timeStyle: 'short' });
};

export default function AdminActionLogsPanel({ lang }: { lang: 'ar' | 'en' }) {
  const supabase = createClient();
  const [logs, setLogs] = useState<AdminActionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchLogs = useCallback(async (off = 0, append = false) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_admin_action_logs', {
        p_limit: PAGE_SIZE + 1,
        p_offset: off,
        p_action_type: actionFilter !== 'all' ? actionFilter : null,
        p_search: search.trim() || null,
        p_date_from: dateFrom ? new Date(dateFrom).toISOString() : null,
        p_date_to: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : null,
      });
      if (error) throw error;
      const rows = (data || []) as AdminActionLog[];
      const more = rows.length > PAGE_SIZE;
      const trimmed = more ? rows.slice(0, PAGE_SIZE) : rows;
      setHasMore(more);
      setLogs(prev => append ? [...prev, ...trimmed] : trimmed);
      setOffset(off);
    } catch {
      try {
        let q = supabase
          .from('admin_action_logs')
          .select('*')
          .order('created_at', { ascending: false })
          .range(off, off + PAGE_SIZE);
        if (actionFilter !== 'all') q = q.eq('action_type', actionFilter);
        if (search.trim()) q = q.ilike('description', `%${search.trim()}%`);
        const { data } = await q;
        const rows = (data || []) as AdminActionLog[];
        setLogs(prev => append ? [...prev, ...rows] : rows);
        setHasMore(rows.length === PAGE_SIZE + 1);
      } catch {
        setLogs([]);
      }
    } finally {
      setLoading(false);
    }
  }, [supabase, actionFilter, search, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(0); }, [fetchLogs]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0D1117] flex items-center gap-2">
            <Activity size={18} className="text-[#0F5C5C]" />
            {lang === 'ar' ? 'سجل إجراءات المدير' : 'Admin Action Logs'}
          </h2>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {lang === 'ar' ? 'تتبع جميع الإجراءات التي يقوم بها مدير المنصة' : 'Track all actions performed by platform admin'}
          </p>
        </div>
        <button
          onClick={() => fetchLogs(0)}
          className="flex items-center gap-1.5 h-9 px-3 bg-[#EAF4F4] text-[#0F5C5C] rounded-xl text-sm font-semibold hover:bg-[#D0E8E8] transition-colors"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          {lang === 'ar' ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={13} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#9CA3AF]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchLogs(0)}
            placeholder={lang === 'ar' ? 'بحث في الوصف...' : 'Search description...'}
            className="w-full h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 pe-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
          />
        </div>
        <select
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value)}
          className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
        >
          <option value="all">{lang === 'ar' ? 'كل الإجراءات' : 'All Actions'}</option>
          <option value="company_activated">{lang === 'ar' ? 'تفعيل شركة' : 'Company Activated'}</option>
          <option value="company_suspended">{lang === 'ar' ? 'تعليق شركة' : 'Company Suspended'}</option>
          <option value="company_deleted">{lang === 'ar' ? 'حذف شركة' : 'Company Deleted'}</option>
          <option value="company_password_change">{lang === 'ar' ? 'تغيير كلمة مرور' : 'Password Change'}</option>
          <option value="join_request_approved">{lang === 'ar' ? 'قبول طلب' : 'Request Approved'}</option>
          <option value="join_request_rejected">{lang === 'ar' ? 'رفض طلب' : 'Request Rejected'}</option>
        </select>
        <input
          type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
          title={lang === 'ar' ? 'من تاريخ' : 'From date'}
        />
        <input
          type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
          title={lang === 'ar' ? 'إلى تاريخ' : 'To date'}
        />
        <button
          onClick={() => fetchLogs(0)}
          className="h-9 px-3 bg-[#0F5C5C] text-white rounded-xl text-sm font-semibold hover:bg-[#0A3D3D] transition-colors flex items-center gap-1.5"
        >
          <Filter size={13} />
          {lang === 'ar' ? 'تطبيق' : 'Apply'}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-[#0F5C5C]" />
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#9CA3AF]">
            <Activity size={32} className="mb-2 opacity-40" />
            <p className="text-sm">{lang === 'ar' ? 'لا توجد سجلات إجراءات بعد' : 'No action logs yet'}</p>
            <p className="text-xs mt-1">{lang === 'ar' ? 'ستظهر الإجراءات هنا بعد تطبيق migration قاعدة البيانات' : 'Actions will appear here after applying the database migration'}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#F0F2F5] bg-[#F7F8FA]">
                    <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الإجراء' : 'Action'}</th>
                    <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'المدير' : 'Admin'}</th>
                    <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الهدف' : 'Target'}</th>
                    <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الوصف' : 'Description'}</th>
                    <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الحالة' : 'Status'}</th>
                    <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'التاريخ' : 'Date'}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => {
                    const cfg = getActionConfig(log.action_type);
                    const ActionIcon = cfg.icon;
                    return (
                      <tr key={log.id} className="border-b border-[#F7F8FA] hover:bg-[#FAFBFC] transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                              <ActionIcon size={13} className={cfg.color} />
                            </div>
                            <span className="text-xs font-semibold text-[#0D1117]">
                              {lang === 'ar' ? cfg.labelAr : cfg.labelEn}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B7280]">{log.admin_email || '—'}</td>
                        <td className="px-4 py-3">
                          {log.target_name ? (
                            <div>
                              <p className="text-xs font-semibold text-[#0D1117]">{log.target_name}</p>
                              {log.target_type && <p className="text-[10px] text-[#9CA3AF]">{log.target_type}</p>}
                            </div>
                          ) : (
                            <span className="text-xs text-[#9CA3AF]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[200px] truncate" title={log.description}>
                          {log.description}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            log.status === 'success' ? 'bg-green-100 text-green-700' :
                            log.status === 'failed'? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {log.status === 'success' ? (lang === 'ar' ? 'نجاح' : 'Success') :
                             log.status === 'failed' ? (lang === 'ar' ? 'فشل' : 'Failed') :
                             (lang === 'ar' ? 'تحذير' : 'Warning')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B7280]">{formatDate(log.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="p-4 border-t border-[#F0F2F5] flex justify-center">
                <button
                  onClick={() => fetchLogs(offset + PAGE_SIZE, true)}
                  disabled={loading}
                  className="flex items-center gap-1.5 h-9 px-4 bg-[#EAF4F4] text-[#0F5C5C] rounded-xl text-sm font-semibold hover:bg-[#D0E8E8] transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 size={13} className="animate-spin" /> : <ChevronDown size={13} />}
                  {lang === 'ar' ? 'تحميل المزيد' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
